import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canRunAudit, formatDate } from "@/lib/permissions";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, ClipboardCheck, CheckCircle2, XCircle, MapPin, AlertTriangle, ScanLine, FileDown, Lock } from "lucide-react";
import QRScannerButton from "@/components/QRScannerButton";

/** Audit list page */
export default function AuditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const canRun = canRunAudit(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get("/audits");
      setAudits(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!canRun) {
    return (
      <div className="text-center py-16 text-slate-500 text-sm">
        <Lock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        You do not have permission to run physical audits.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Verification</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Physical Audits</h1>
          <p className="text-slate-500 mt-1 text-sm">{audits.length} audit session{audits.length === 1 ? "" : "s"}</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="new-audit-btn">
          <Plus className="w-4 h-4 mr-2" /> New Audit
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Verified</th>
                <th className="px-4 py-3">Discrepancies</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
              ) : audits.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-16 text-center">
                  <ClipboardCheck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <div className="text-slate-500">No audits yet. Start your first physical verification.</div>
                </td></tr>
              ) : audits.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/audits/${a.id}`)} data-testid={`audit-row-${a.code}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A]">{a.code}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDate(a.created_at)}<div>{a.created_by_name}</div></td>
                  <td className="px-4 py-3 font-mono">{a.summary?.verified ?? 0} / {a.summary?.total ?? "—"}</td>
                  <td className="px-4 py-3">
                    {(a.summary?.damaged || 0) + (a.summary?.found_elsewhere || 0) + (a.summary?.not_found || 0) > 0 ? (
                      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                        {(a.summary?.damaged || 0) + (a.summary?.found_elsewhere || 0) + (a.summary?.not_found || 0)} issues
                      </Badge>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={a.status === "closed" ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                      {a.status === "closed" ? "Closed" : "In progress"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {addOpen && (
        <NewAuditDialog open={addOpen} onOpenChange={setAddOpen}
          onDone={(id) => { setAddOpen(false); navigate(`/audits/${id}`); }} />
      )}
    </div>
  );
}

function NewAuditDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: `Physical Audit — ${new Date().toISOString().slice(0, 10)}`, notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/audits", form);
      toast.success(`Audit ${data.code} started`);
      onDone(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">New Physical Audit</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label>Audit Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-audit-name" /></div>
          <div><Label>Notes / Scope</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Q1 FY26-27 physical verification — Plant A" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.name.trim()} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="confirm-new-audit-btn">
            {busy ? "Starting..." : "Start Audit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Audit detail page — scan/enter asset IDs, mark status */
export function AuditDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetInput, setAssetInput] = useState("");
  const [statusInput, setStatusInput] = useState("verified");
  const [actualLocation, setActualLocation] = useState("");
  const [notesInput, setNotesInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/audits/${id}`);
      setAudit(data.audit);
      setEntries(data.entries || []);
    } catch { toast.error("Failed to load audit"); navigate("/audits"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const verify = async () => {
    const q = assetInput.trim();
    if (!q) return;
    setBusy(true);
    try {
      await api.post(`/audits/${id}/verify`, {
        asset_query: q, status: statusInput,
        actual_location: statusInput === "found_elsewhere" ? actualLocation : null,
        notes: notesInput || null,
      });
      toast.success(`${q} · ${statusInput}`);
      setAssetInput(""); setActualLocation(""); setNotesInput(""); setStatusInput("verified");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verify failed");
    } finally { setBusy(false); }
  };

  const closeAudit = async () => {
    if (!confirm("Close this audit? Any unaccounted active assets will be flagged as Not Found in the report.")) return;
    try {
      await api.post(`/audits/${id}/close`);
      toast.success("Audit closed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}/audits/${id}/report.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${audit.code.replace(/[\/\\]/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Failed to download report"); }
  };

  if (loading) return <div className="text-slate-400 text-center py-12">Loading...</div>;
  if (!audit) return null;

  const isOpen = audit.status !== "closed";
  const s = audit.summary || {};

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="audit-detail-page">
      <button onClick={() => navigate("/audits")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to Audits
      </button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Physical Audit</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 font-mono">{audit.code}</h1>
          <div className="text-sm text-slate-700 mt-1">{audit.name}</div>
          <div className="text-xs text-slate-500 mt-1">Started {formatDate(audit.created_at)} by {audit.created_by_name}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadReport} data-testid="download-audit-btn"><FileDown className="w-4 h-4 mr-2" /> Report PDF</Button>
          {isOpen && (
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={closeAudit} data-testid="close-audit-btn">Close Audit</Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: "Total", v: s.total || 0, cls: "text-slate-900" },
          { l: "Verified", v: s.verified || 0, cls: "text-emerald-700" },
          { l: "Damaged", v: s.damaged || 0, cls: "text-amber-700" },
          { l: "Wrong Loc.", v: s.found_elsewhere || 0, cls: "text-orange-700" },
          { l: "Not Found", v: s.not_found || 0, cls: "text-red-700" },
        ].map((c) => (
          <Card key={c.l} className="p-4"><div className="text-xs text-slate-500">{c.l}</div><div className={`font-heading text-2xl font-bold mt-1 ${c.cls}`}>{c.v}</div></Card>
        ))}
      </div>

      {isOpen && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><ScanLine className="w-4 h-4 text-[#7C1D3A]" /> Verify Asset</CardTitle></CardHeader>
          <CardContent className="p-6 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Asset ID or scan result *</Label>
                  <QRScannerButton
                    label="Scan QR"
                    size="sm"
                    onDetected={(txt) => { setAssetInput(txt); toast.success(`Scanned: ${txt}`); }}
                  />
                </div>
                <Input autoFocus value={assetInput} onChange={(e) => setAssetInput(e.target.value)} placeholder="e.g. MAC-CNCM-00001 (or use scanner)" onKeyDown={(e) => e.key === "Enter" && !busy && verify()} data-testid="audit-asset-input" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={statusInput} onValueChange={setStatusInput}>
                  <SelectTrigger data-testid="audit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">Verified — In place</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="found_elsewhere">Found elsewhere</SelectItem>
                    <SelectItem value="not_found">Not Found</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={verify} disabled={busy || !assetInput.trim()} className="bg-[#7C1D3A] hover:bg-[#6c1c36] w-full" data-testid="audit-verify-btn">
                  {busy ? "Recording..." : "Record"}
                </Button>
              </div>
            </div>
            {statusInput === "found_elsewhere" && (
              <div><Label>Actual Location *</Label><Input value={actualLocation} onChange={(e) => setActualLocation(e.target.value)} data-testid="audit-actual-location" /></div>
            )}
            <div><Label>Notes</Label><Input value={notesInput} onChange={(e) => setNotesInput(e.target.value)} placeholder="Optional observation" /></div>
          </CardContent>
        </Card>
      )}

      {/* Entries */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Verified Entries ({entries.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">No verifications yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Asset</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3">By</th>
                  <th className="px-4 py-3">At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{e.asset_snapshot?.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{e.asset_snapshot?.asset_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={
                        e.status === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        e.status === "damaged" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        e.status === "found_elsewhere" ? "bg-orange-50 text-orange-700 border-orange-200" :
                        "bg-red-50 text-red-700 border-red-200"
                      }>{e.status.replace("_", " ")}</Badge>
                      {e.actual_location && <div className="text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3 inline" /> {e.actual_location}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{e.notes || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{e.verified_by_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDate(e.verified_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
